import type { FontAssetMetadata } from '@course-authoring/shared-types';
import { fetchWithAuthRetry, handleApiResponse } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export type ThemeAssetKind = 'logo' | 'font';

/** Expanded asset returned in AccountThemeDto and from complete-upload. */
export interface ThemeAssetDto {
  id: number;
  url: string;
  contentType: string;
  filename: string;
  /** Font weights extracted at upload; null for logos and fonts uploaded before NA-453. */
  metadata?: FontAssetMetadata | null;
}

interface PrepareUploadResponse {
  asset: {
    id: number;
    externalAccountId: number | null;
    kind: ThemeAssetKind;
    storageUuid: string;
    status: 'pending_upload';
    storagePrefix: string;
    contentType: string;
    originalFilename: string;
  };
  uploadUrl: string;
  s3Key: string;
  /** Canonical content-type from the server — use this when PUTing to S3. */
  contentType: string;
}

/**
 * Uploads a file directly to S3 using a presigned PUT URL.
 * Uses the server-returned content-type (overrides file.type) so S3 stores
 * the correct MIME type regardless of what the browser reports.
 */
async function uploadToS3(presignedUrl: string, file: File, contentType: string): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!response.ok) {
    throw new Error(`Failed to upload to S3: ${response.statusText}`);
  }
}

/**
 * Full three-step upload: prepare → PUT to S3 → complete.
 * Returns the completed ThemeAssetDto.
 */
export async function uploadThemeAsset(params: {
  externalAccountId: number | null;
  kind: ThemeAssetKind;
  file: File;
}): Promise<ThemeAssetDto> {
  // 1. Prepare: get presigned URL + create pending row
  const prepareResponse = await fetchWithAuthRetry(
    `${API_BASE_URL}/theme-assets/prepare-upload`,
    {
      method: 'POST',
      body: JSON.stringify({
        externalAccountId: params.externalAccountId,
        kind: params.kind,
        filename: params.file.name,
        contentType: params.file.type,
      }),
    },
  );
  const prepared = await handleApiResponse<PrepareUploadResponse>(prepareResponse, 'Failed to prepare theme asset upload');

  // 2. Upload file directly to S3 using the server-returned content-type
  await uploadToS3(prepared.uploadUrl, params.file, prepared.contentType);

  // 3. Complete: mark as active
  const completeResponse = await fetchWithAuthRetry(
    `${API_BASE_URL}/theme-assets/${prepared.asset.id}/complete-upload`,
    {
      method: 'PUT',
      body: JSON.stringify({ fileSizeBytes: params.file.size }),
    },
  );
  return handleApiResponse<ThemeAssetDto>(completeResponse, 'Failed to complete theme asset upload');
}
